import type { CommandPaletteItem } from "./controller.ts";

type Action = () => void | Promise<void>;
export interface DiscoveryCapabilities {
  appearance: Action;
  dateFormat: Action;
  account: Action;
  help: Action;
  myDay: Action;
  today: Action;
  weeklyReview: Action;
  organizeInbox: Action;
  priorityMatrix: Action;
  invitations: Action;
  spaceSharing?: Action;
  collection?: { name: string; settings?: Action; share?: Action };
  taskSchedule?: Action;
  newTaskSchedule?: Action;
}

/** Feature aliases lead to real, existing controls; no duplicate settings UI. */
export function discoveryCommands(actions: DiscoveryCapabilities): CommandPaletteItem[] {
  const setting = (id: string, label: string, description: string, keywords: string, run: Action): CommandPaletteItem => ({ id, label, description, keywords, run, hint: "", group: "Settings" });
  const action = (id: string, label: string, description: string, keywords: string, run: Action): CommandPaletteItem => ({ id, label, description, keywords, run, hint: "", group: "Actions" });
  return [
    setting("appearance", "Appearance", "Theme, density and mobile action bar · This device", "dark light contrast font display", actions.appearance),
    setting("date-format", "Change date format", "Choose how dates are displayed · This device", "dates day month year yyyy dd mm locale", actions.dateFormat),
    setting("account", "Account and synchronization", "Account, sessions and device recovery", "sync offline failed attention password security login", actions.account),
    setting("help", "Getting started", "Spaces, collections, quick capture and offline use", "help guide gestures instructions", actions.help),
    setting("sharing-invitations", "Sharing invitations", "Review invitations to shared work", "sharing invite access collaboration permissions", actions.invitations),
    ...(actions.spaceSharing ? [setting("space-sharing", "Share current space", "Manage who can access this space and its collections", "sharing invite editor viewer permissions collaboration", actions.spaceSharing)] : []),
    action("my-day", "Open My Day", "Tasks you personally choose to work on · Only you", "plan day daily personal", actions.myDay),
    action("today", "Open Today", "Tasks due today in the current location", "deadline due scheduled", actions.today),
    action("weekly-review", "Weekly review", "Review a group of tasks and decide what needs attention", "weekly organize overdue blocked inactive", actions.weeklyReview),
    action("organize-inbox", "Organize inbox", "Review tasks and choose a collection when needed", "triage unfiled no collection", actions.organizeInbox),
    action("priority-matrix", "Priority matrix", "Understand urgency and importance", "urgent important eisenhower priorities", actions.priorityMatrix),
    ...(actions.collection?.settings ? [setting("collection-settings", "Edit collection settings", `${actions.collection.name} · Changes affect this collection`, "board timeline gantt columns defaults automation wip density", actions.collection.settings)] : []),
    ...(actions.collection?.share ? [setting("collection-sharing", "Share collection", `${actions.collection.name} · Manage access`, "sharing invite editor viewer permissions collaboration", actions.collection.share)] : []),
    ...(actions.taskSchedule ? [action("task-schedule", "Edit task scheduling", "Dates, reminders and repeating tasks · Current task", "repeat repeating recurring recurrence frequency reminder schedule", actions.taskSchedule)] : actions.newTaskSchedule ? [action("new-task-schedule", "Create a repeating task", "Opens the Repeat options · Nothing is saved until you save", "repeat repeating recurring recurrence frequency reminder schedule", actions.newTaskSchedule)] : []),
  ];
}

/** Reveal a real control even when it lives inside progressive disclosure. */
export function revealControl(target: HTMLElement | null): void {
  if (!target) return;
  for (let ancestor = target.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (ancestor.tagName === "DETAILS") (ancestor as HTMLDetailsElement).open = true;
  }
  target.focus();
  target.scrollIntoView({ block: "nearest" });
}
